import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    const savedBranch = localStorage.getItem('currentBranch');
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
        if (savedBranch) {
          setCurrentBranch(savedBranch);
        }
      } catch (err) {
        console.error('Failed to parse saved user:', err);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('currentBranch');
      }
    }
  }, []);

  // Login
  const login = useCallback(async (staffId, pin) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        staff_id: staffId,
        pin: pin
      });
      if (response.data.success) {
        setCurrentUser(response.data.staff);
        setCurrentBranch(response.data.staff.branch);
        // Save to localStorage
        localStorage.setItem('currentUser', JSON.stringify(response.data.staff));
        localStorage.setItem('currentBranch', response.data.staff.branch);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return { success: false, message: 'Chyba připojení k serveru' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    setCurrentUser(null);
    setCurrentBranch(null);
    setCart([]);
    // Clear localStorage
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentBranch');
  }, []);

  // Cart operations
  const addToCart = useCallback((service) => {
    setCart(prev => [...prev, { ...service, cartId: Date.now() + Math.random() }]);
  }, []);

  const removeFromCart = useCallback((cartId) => {
    setCart(prev => prev.filter(item => item.cartId !== cartId));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const getCartTotal = useCallback(() => {
    return cart.reduce((sum, item) => sum + item.price, 0);
  }, [cart]);

  const normalizeAmount = useCallback((value) => {
    const amount = Math.round(Number(value) || 0);
    return amount > 0 ? amount : 0;
  }, []);

  // Create order
  const createOrder = useCallback(async (paymentMethod, tipAmount = 0) => {
    if (!currentUser || cart.length === 0) return { success: false };
    
    setIsLoading(true);
    try {
      const normalizedTipAmount = normalizeAmount(tipAmount);
      const subtotal = getCartTotal();
      const orderData = {
        staff_id: currentUser.id,
        staff_name: currentUser.name,
        branch: currentBranch,
        items: cart.map(item => ({
          service_id: item.id,
          service_name: item.name,
          price: item.price
        })),
        subtotal,
        tip_amount: normalizedTipAmount,
        total: subtotal + normalizedTipAmount,
        payment_method: paymentMethod
      };
      
      await axios.post(`${API}/orders`, orderData);
      clearCart();
      return { success: true };
    } catch (error) {
      return { success: false, message: 'Chyba při vytváření objednávky' };
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, currentBranch, cart, getCartTotal, clearCart, normalizeAmount]);

  const value = {
    currentUser,
    currentBranch,
    cart,
    isLoading,
    login,
    logout,
    addToCart,
    removeFromCart,
    clearCart,
    getCartTotal,
    createOrder,
    setCurrentBranch
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
